import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { RiArrowDownSLine, RiFileList3Line, RiCalendarLine } from 'react-icons/ri';
import dayjs from 'dayjs';
import axios from 'axios';
import PaginationComponent from '../../../utils/PaginationComponent';
import TableSkeletonLoader from '../../../utils/TableSkeletonLoader';
import SearchInput from '../../../utils/SearchInput';
import searchIcon from "../../../assets/admin/search.svg";
import viewIcon from "../../../assets/admin/view.svg";
import AllOrderView from './AllOrderView';
import DashboardTooltip from '../Components/DashboardTooltip';

const AllOrderManagement = ((props, ref) => {
    const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
    const token = localStorage.getItem("token");
    const [isLoading, setIsLoading] = useState(false);
    const [isViewOrderLoading, setIsViewOrderLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [showOrder, setShowOrder] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    const [orderData, setOrderData] = useState([]);
    const [viewOrderData, setViewOrderData] = useState({});

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [searchQuery, setSearchQuery] = useState("");
    const [searchRestQuery, setSearchRestQuery] = useState("");
    const [showOption, setShowOption] = useState(false);
    const [filteredRestArray, setFilteredRestArray] = useState([]);
    const [selectedRestId, setSelectedRestId] = useState(null);

    const getRestaurantData = async () => {
        setIsLoading2(true);
        try {
            const params = {
                pageNumber: page,
                size: 25,
                search: searchRestQuery,
            };
            const response = await axios.get(`${apiUrl}/api/v1/restaurants`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            setFilteredRestArray(response?.data?.data || []);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setIsLoading2(false);
        }
    }

    useEffect(() => {
        getRestaurantData();
    }, [searchRestQuery])

    const getOrdersData = async () => {
        setIsLoading(true);
        try {
            let params;
            if (searchQuery) {
                params = {
                    pageNumber: page,
                    size: rowsPerPage,
                    search: searchQuery,
                    restaurant_id: selectedRestId,
                };
            } else {
                params = {
                    pageNumber: page,
                    size: rowsPerPage,
                    restaurant_id: selectedRestId,
                };
            }
            const response = await axios.get(`${apiUrl}/api/v1/all-orders`, {
                params,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = response.data;
            setOrderData(data.data);
            setTotalPages(data.total || 0);
        } catch (error) {
            console.error("Error fetching order data:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getViewOrdersData = async (orderId) => {
        if (!orderId) return;
        setIsViewOrderLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/api/v1/orders/${orderId}/wines`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });

            const data = response.data;
            setViewOrderData(data.wines?.data || []);
        } catch (error) {
            console.error("Error fetching or updating order data:", error);
        } finally {
            setIsViewOrderLoading(false);
        }
    };

    const handleRestSelection = (wineId) => {
        setSelectedRestId((prevRestId) => (prevRestId === wineId ? null : wineId));
        setShowOption(false);
        setPage(1);
    };

    const handleSearchChange = (value) => {
        setSearchQuery(value);
        setPage(1);
    };

    const handleViewOrderClick = (order) => {
        setSelectedOrder(order);
        setShowOrder(true);
        getViewOrdersData(order?.id);
    };

    const handleBackFromOrder = () => {
        setShowOrder(false);
        setSelectedOrder(null);
        setViewOrderData([]);
        setIsViewOrderLoading(false);
        getOrdersData();
    };

    // Helper to render premium status badge
    const renderStatusBadge = (status) => {
        const raw = String(status || "").trim();
        const lower = raw.toLowerCase();

        let label = raw || "N/A";
        let badgeStyle = "bg-gray-50 text-gray-700 border-gray-200";
        let dotColor = "bg-gray-400";

        if (lower === "accepted") {
            label = "Accettato";
            badgeStyle = "bg-emerald-50 text-emerald-800 border-emerald-200";
            dotColor = "bg-emerald-500";
        } else if (lower === "refused") {
            label = "Rifiutato";
            badgeStyle = "bg-rose-50 text-rose-800 border-rose-200";
            dotColor = "bg-rose-500";
        } else if (lower === "transmitted") {
            label = "Trasmesso";
            badgeStyle = "bg-blue-50 text-blue-800 border-blue-200";
            dotColor = "bg-blue-500";
        } else if (lower === "received") {
            label = "Ricevuto";
            badgeStyle = "bg-purple-50 text-purple-800 border-purple-200";
            dotColor = "bg-purple-500";
        } else if (lower === "in transit") {
            label = "In Transito";
            badgeStyle = "bg-amber-50 text-amber-800 border-amber-200";
            dotColor = "bg-amber-500";
        }

        if (!raw) {
            return <span className="text-gray-400 text-xs">N/A</span>;
        }

        return (
            <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-semibold tracking-wide uppercase rounded-full border shadow-2xs ${badgeStyle}`}>
                <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${dotColor}`}></span>
                {label}
            </span>
        );
    };

    useImperativeHandle(ref, () => ({
        order() {
            setShowOrder(false);
            setSelectedOrder(null);
            setViewOrderData([]);
            setIsViewOrderLoading(false);
            getOrdersData();
        },
    }));

    useEffect(() => {
        getOrdersData();
    }, [page, rowsPerPage, selectedRestId, searchQuery]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (!event.target.closest(".dropdown-container")) {
                setShowOption(false);
            }
        };
        document.addEventListener("click", handleOutsideClick);
        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, []);

    return (
        <>
            <section className="w-full">
                {showOrder && selectedOrder ? (
                    <>
                        <div className="flex sm:flex-row flex-col sm:justify-between sm:items-center items-start gap-3 sm:gap-4 mt-3 mb-5 w-full">
                            <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-3 min-w-0 w-full sm:w-auto">
                                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold poppins-medium text-[#303030] whitespace-nowrap shrink-0">
                                    Dettaglio Ordine
                                </h3>
                                {Boolean(selectedOrder?.order_number) && (
                                    <div className="flex sm:inline-flex items-center justify-between sm:justify-start gap-2 px-3.5 py-1.5 rounded-full bg-[#FAF3F4] border border-[#8B2433]/25 shadow-xs w-full sm:w-auto min-w-0">
                                        <div className="flex items-center gap-2 min-w-0 flex-1 sm:flex-initial">
                                            <RiFileList3Line className="w-4 h-4 text-[#8B2433] shrink-0" />
                                            <span className="text-xs sm:text-sm font-semibold font-mono poppins-medium text-[#8B2433] shrink-0">
                                                {selectedOrder?.order_number}
                                            </span>
                                            {((selectedOrder?.restaurant?.company_name && selectedOrder?.restaurant?.company_name !== "null") || (selectedOrder?.restaurant?.restaurant_name && selectedOrder?.restaurant?.restaurant_name !== "null")) && (
                                                <div className="border-l border-[#8B2433]/25 pl-2 min-w-0 flex-1 sm:flex-initial sm:max-w-[180px] md:max-w-[240px] lg:max-w-[300px] truncate text-xs sm:text-sm font-semibold poppins-medium text-[#8B2433]">
                                                    <DashboardTooltip
                                                        text={
                                                            (selectedOrder?.restaurant?.company_name && selectedOrder?.restaurant?.company_name !== "null")
                                                                ? selectedOrder?.restaurant?.company_name
                                                                : selectedOrder?.restaurant?.restaurant_name
                                                        }
                                                        maxLength={30}
                                                        className="text-xs sm:text-sm font-semibold poppins-medium text-[#8B2433] truncate"
                                                    />
                                                </div>
                                            )}
                                        </div>
                                        {Boolean(selectedOrder?.status) && (
                                            <span className="shrink-0 ml-1.5 sm:ml-0">
                                                {renderStatusBadge(selectedOrder?.status)}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center justify-between sm:justify-start gap-2.5 sm:gap-3 shrink-0 w-full sm:w-auto">
                                {selectedOrder?.order_date && (
                                    <div className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-3.5 py-[6px] rounded-full bg-[#FAF3F4] border border-[#8B2433]/25 shadow-xs text-xs sm:text-sm font-medium text-[#8B2433] poppins-medium whitespace-nowrap shrink-0">
                                        <RiCalendarLine className="w-4 h-4 text-[#8B2433] shrink-0" />
                                        <span className="whitespace-nowrap">
                                            {dayjs(selectedOrder.order_date).isValid()
                                                ? dayjs(selectedOrder.order_date).format("DD-MM-YYYY")
                                                : selectedOrder.order_date}
                                        </span>
                                    </div>
                                )}
                                <button
                                    onClick={handleBackFromOrder}
                                    className="rounded-full py-[6px] border-[2px] bg-[#fff] border-[#F2C3CA] px-4 sm:px-5 text-[#8B2433] text-xs sm:text-sm font-medium shadow-sm poppins-regular whitespace-nowrap shrink-0 hover:bg-[#8B2433] hover:text-white transition-colors cursor-pointer"
                                >
                                    Torna indietro
                                </button>
                            </div>
                        </div>
                        <AllOrderView
                            viewOrderData={viewOrderData}
                            isLoading={isViewOrderLoading}
                        />
                    </>
                ) : (
                    <>
                        <div className="flex lg:flex-row flex-col justify-between items-center mt-3 mb-5 w-full">
                            <h3 className="text-3xl font-bold mb-3 xl:mb-0 poppins-medium self-start">Tutti Gli Ordini</h3>
                            <div className="self-start flex items-center gap-4 md:flex-row flex-col sm:w-auto w-full">
                                <SearchInput
                                    onSearchChange={handleSearchChange}
                                    component="Order Management"
                                    placeholder="Ordini"
                                />
                                <div className="w-full sm:w-auto flex flex-col lg:flex-row gap-2 dropdown-container">
                                    <div className="relative group w-full sm:w-[300px] md:w-[320px] sm:min-w-[280px]">
                                        <button
                                            className={`${showOption ? "border-b-0 rounded-tl-3xl rounded-tr-3xl" : "rounded-full"} py-[7px] border-[2px] bg-[#fff] border-[#F2C3CA] inline-flex justify-between items-center w-full px-4 outline-none text-[#8B2433] font-medium shadow-sm relative z-30`}
                                            onClick={() => setShowOption(!showOption)}
                                        >
                                            {/* <span className="mr-2 flex items-center gap-3 poppins-regular">
                                                {selectedRestId
                                                    ? (filteredRestArray.find((rest) => rest.id === selectedRestId)?.restaurant_name?.length > 25
                                                        ? filteredRestArray.find((rest) => rest.id === selectedRestId)?.restaurant_name?.slice(0, 25) + "..."
                                                        : filteredRestArray.find((rest) => rest.id === selectedRestId)?.restaurant_name) || "Scegli Ristorante"
                                                    : "Scegli Ristorante"}
                                            </span> */}
                                            <span className="mr-2 block poppins-regular text-[13px] sm:text-sm truncate flex-1 text-left">
                                                {selectedRestId
                                                    ? (() => {
                                                        const selectedRestaurant = filteredRestArray.find((rest) => rest.id === selectedRestId);
                                                        const companyName = selectedRestaurant?.company_name;
                                                        const restaurantCode = selectedRestaurant?.code;

                                                        if (companyName && companyName !== "null") {
                                                            return companyName.length > 32
                                                                ? `${companyName.slice(0, 32)}...`
                                                                : companyName;
                                                        } else if (restaurantCode) {
                                                            return restaurantCode;
                                                        } else {
                                                            return "Scegli Ristorante";
                                                        }
                                                    })()
                                                    : "Scegli Ristorante"}
                                            </span>
                                            <RiArrowDownSLine
                                                className={`transform transition-transform duration-300 ${showOption ? "rotate-180" : ""}`}
                                                size={"24px"}
                                                color="#9f414f"
                                            />

                                        </button>
                                        <div
                                            className={`
${showOption ? "opacity-100 scale-100 visible z-50" : "opacity-0 scale-95 invisible z-20"}
bg-[#fff] h-[200px] border-[#F2C3CA] overflow-y-scroll absolute pt-[9px] rounded-t-none 
border-t-0 p-2 px-4 right-0 mt-0 rounded-[21px] shadow-lg text-[#DB7685] space-y-1 
w-full border-[2px] border-[#8B24335C] 
transition-all duration-300 ease-in-out transform origin-top
`}
                                        >
                                            <div className="flex px-[10px] py-[5px] flex-row relative border rounded-[28px] bg-[#f6efef66] my-[5px] justify-around gap-x-2">
                                                <img
                                                    src={searchIcon}
                                                    className="block left-0 top-0 bottom-0 my-auto h-4 w-4"
                                                    alt="Search"
                                                />
                                                <input
                                                    type="text"
                                                    className="block poppins-regular bg-transparent outline-none cursor-pointer w-full text-xs placeholder:text-xs"
                                                    placeholder="Scegli Ristorante"
                                                    value={searchRestQuery}
                                                    onChange={(e) => setSearchRestQuery(e.target.value)}
                                                />
                                            </div>
                                            {isLoading2 ? (
                                                <div role="status" className="flex justify-center items-center h-full">
                                                    <svg
                                                        aria-hidden="true"
                                                        className="w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-[#8B2433]"
                                                        viewBox="0 0 100 101"
                                                        fill="none"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                    >
                                                        <path
                                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                                            fill="currentColor"
                                                        />
                                                        <path
                                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                                            fill="currentFill"
                                                        />
                                                    </svg>
                                                    <span className="sr-only poppins-medium">Loading...</span>
                                                </div>
                                            ) : (
                                                <>
                                                    {filteredRestArray && filteredRestArray?.length > 0 ? (
                                                        filteredRestArray?.map((rest) => (
                                                            <label
                                                                htmlFor=""
                                                                key={rest?.id}
                                                                className="border-b border-[#8b24331f] px-0 py-2 flex align-center justify-between flex-row-reverse gap-[15px] filter_list_btn cursor-pointer"
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    name="wineSelection"
                                                                    className="outline-none cursor-pointer flex-shrink-0"
                                                                    checked={selectedRestId === rest?.id}
                                                                    onChange={() => handleRestSelection(rest?.id)}
                                                                />
                                                                <span className="text-[#303030] rounded-md block truncate poppins-regular text-xs text-left flex-1 ml-1">
                                                                    {rest?.company_name && rest?.company_name !== "null" ? rest?.company_name : rest?.code}
                                                                </span>
                                                            </label>
                                                        ))
                                                    ) : (
                                                        <p className="text-gray-500 text-sm">No Restaurants available.</p>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="w-auto dash-table-main h-auto bg-[#FBFBFB] border-[2px] border-[#8B24332B] rounded-xl shadow overflow-hidden overflow-x-auto">
                            <table className="md:w-full min-w-[900px] text-left wine_table">
                                <thead>
                                    <tr className="border-b-2 border-[#8B2433]/30 bg-[#FAF3F4]">
                                        <th className="py-3.5 px-3 first:pl-6 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Sr.no</th>
                                        <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Nome Ristorante</th>
                                        <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Codice</th>
                                        <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">N.Ordine</th>
                                        <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Data Ordine</th>
                                        <th className="py-3.5 px-3 text-left poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Stato Ordine</th>
                                        <th className="py-3.5 px-3 last:pr-6 text-center poppins-semibold text-[#8B2433] text-[13px] sm:text-sm whitespace-nowrap">Azione</th>
                                    </tr>
                                </thead>
                                {isLoading ? (
                                    <TableSkeletonLoader
                                        columns={7}
                                        rows={rowsPerPage || 10}
                                        minWidths={["60px", "220px", "110px", "130px", "130px", "130px", "90px"]}
                                        actionCount={1}
                                    />
                                ) : (
                                    <tbody>
                                        {orderData && orderData?.length > 0 ? (
                                            orderData
                                                .map((order, index) => {
                                                    const serialNumber = (page - 1) * rowsPerPage + index + 1;
                                                    return (
                                                        <tr key={order.id} className="border-b border-[#8B2433]/12 hover:bg-[#8B2433]/[0.03] transition-colors duration-150">
                                                            <td className="py-4 px-3 first:pl-6 text-left poppins-medium text-[#303030] whitespace-nowrap text-sm">{serialNumber}</td>
                                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] uppercase whitespace-nowrap text-sm">
                                                                <DashboardTooltip
                                                                    text={
                                                                        (order?.restaurant?.company_name && order?.restaurant?.company_name !== "null")
                                                                            ? order?.restaurant?.company_name
                                                                            : (order?.restaurant?.restaurant_name && order?.restaurant?.restaurant_name !== "null")
                                                                                ? order?.restaurant?.restaurant_name
                                                                                : "N/A"
                                                                    }
                                                                />
                                                            </td>
                                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] whitespace-nowrap">
                                                                <span className="text-gray-500 font-mono text-xs">
                                                                    {order?.restaurant?.code || "N/A"}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-3 text-left poppins-medium whitespace-nowrap">
                                                                <span className="inline-flex items-center justify-center px-2.5 py-[3px] text-[11px] font-semibold tracking-wide font-mono rounded-md border bg-[#FAF3F4] text-[#8B2433] border-[#8B2433]/25 shadow-2xs">
                                                                    {order?.order_number || "N/A"}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] whitespace-nowrap">
                                                                <span className="text-gray-500 font-mono text-xs">
                                                                    {order?.order_date ? (
                                                                        new Date(order.order_date).toLocaleDateString("en-GB").replace(/\//g, "-")
                                                                    ) : (
                                                                        <span className="text-gray-400">N/A</span>
                                                                    )}
                                                                </span>
                                                            </td>
                                                            <td className="py-4 px-3 text-left poppins-medium text-[#303030] whitespace-nowrap">
                                                                {renderStatusBadge(order?.status)}
                                                            </td>
                                                            <td className="py-4 px-3 last:pr-6 text-center poppins-medium text-[#303030] whitespace-nowrap">
                                                                <div className="flex items-center justify-center gap-2 shrink-0">
                                                                    <button
                                                                        onClick={() => { handleViewOrderClick(order) }}
                                                                        className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg hover:bg-[#8B2433]/10 border border-[#8B2433]/20 transition-all cursor-pointer"
                                                                        title="Visualizza ordine"
                                                                    >
                                                                        <img src={viewIcon} alt="Visualizza ordine" className="w-5 h-5 object-contain" />
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                })
                                        ) : (
                                            <tr>
                                                <td colSpan={7} className="text-center py-8 text-gray-500 poppins-medium">Nessun ordine trovato</td>
                                            </tr>
                                        )}
                                    </tbody>
                                )}
                            </table>
                        </div>
                        {orderData && orderData?.length > 0 && (
                            <PaginationComponent
                                page={page}
                                totalPages={Number(totalPages) || 0}
                                rowsPerPage={rowsPerPage}
                                setPage={setPage}
                                setRowsPerPage={setRowsPerPage}
                            />
                        )}
                    </>
                )}
            </section>
        </>
    );
})

export default forwardRef(AllOrderManagement);